/**
 * Command Bar Component - YUL ON TAP
 * 
 * Global top command bar for all authenticated pages.
 * Provides consistent navigation and actions across the platform.
 */
import React from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { Button } from './ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import LanguageSelector from './LanguageSelector';
import {
  Building2,
  UtensilsCrossed,
  Car,
  HeartPulse,
  Moon,
  Sun,
  User,
  Settings,
  LogOut,
  ChevronDown,
  ArrowLeftRight,
  Shield,
  LayoutDashboard,
  ShieldAlert,
  Link2,
} from 'lucide-react';

// Industry/Workspace configuration
const WORKSPACE_CONFIG = {
  hotel: {
    icon: Building2,
    color: '#3b82f6',
    label: { en: 'Hotels', fr: 'Hôtels' },
  },
  restaurant: {
    icon: UtensilsCrossed,
    color: '#f97316',
    label: { en: 'Restaurants', fr: 'Restaurants' },
  },
  automotive: {
    icon: Car,
    color: '#22c55e',
    label: { en: 'Automotive', fr: 'Automobile' },
  },
  healthcare: {
    icon: HeartPulse,
    color: '#ef4444',
    label: { en: 'Healthcare', fr: 'Santé' },
  },
};

export default function CommandBar({ 
  title,
  subtitle,
  actions,
  showWorkspaceIndicator = true,
  workspace: workspaceProp, // Explicit workspace override
}) {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout, industryPortal, currentWorkspace, hasMultipleWorkspaces } = useAuth();
  const { theme, toggleTheme, isDark } = useTheme();
  
  // Check if we're on the admin/control center page
  const isOnAdminPage = location.pathname === '/admin';
  const isOnHotelsPage = location.pathname.startsWith('/hotels');
  const isOnIncidentsPage = location.pathname === '/hotels/incidents';
  const isOnUrlRegistryPage = location.pathname === '/hotels/url-registry';
  
  // Determine the workspace to display - explicit prop takes priority, then context, then localStorage
  // IMPORTANT: industryPortal from context is the source of truth for SSO logins
  const effectiveIndustry = workspaceProp || industryPortal || localStorage.getItem('yul_industry_portal');
  // Only fall back to currentWorkspace if no industry portal is set (legacy paths)
  const workspace = effectiveIndustry || currentWorkspace || 'hotel';
  const config = WORKSPACE_CONFIG[workspace] || WORKSPACE_CONFIG.hotel;
  const WorkspaceIcon = config.icon;
  
  const handleLogout = () => {
    logout();
    navigate('/hotels/login');
  };

  const handleSwitchWorkspace = () => {
    navigate('/select-vertical');
  };

  return (
    <header className="sticky top-0 z-50 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="max-w-7xl mx-auto px-6">
        <div className="flex items-center justify-between h-16">
          {/* Left Section - Workspace & Title */}
          <div className="flex items-center space-x-4">
            {/* Workspace Indicator */}
            {showWorkspaceIndicator && (
              <div className="flex items-center space-x-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${config.color}15` }}
                >
                  <WorkspaceIcon 
                    className="w-5 h-5" 
                    style={{ color: config.color }} 
                  />
                </div>
                <div className="hidden sm:block">
                  <span className="text-sm font-semibold text-foreground">
                    YUL ON TAP
                  </span>
                  <p className="text-xs text-muted-foreground">
                    {config.label[i18n.language] || config.label.en}
                  </p>
                </div>
              </div>
            )}
            
            {/* Separator */}
            {showWorkspaceIndicator && title && (
              <div className="hidden md:block h-6 w-px bg-border" />
            )}
            
            {/* Page Title */}
            {title && (
              <div className="hidden md:block">
                <h1 className="text-lg font-medium text-foreground">{title}</h1>
                {subtitle && (
                  <p className="text-xs text-muted-foreground">{subtitle}</p>
                )}
              </div>
            )}
          </div>

          {/* Right Section - Actions */}
          <div className="flex items-center space-x-2">
            {/* Custom Actions */}
            {actions && (
              <div className="hidden sm:flex items-center space-x-2 mr-2">
                {actions}
              </div>
            )}
            
            {/* Quick Navigation for Super Admin */}
            {user?.role === 'super_admin' && (
              <>
                {!isOnIncidentsPage && isOnHotelsPage && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/hotels/incidents')}
                    data-testid="cmd-incidents"
                    className="hidden sm:flex border-red-500/30 text-red-400 hover:bg-red-500/10"
                  >
                    <ShieldAlert className="w-4 h-4 mr-2" />
                    {i18n.language === 'fr' ? 'Incidents' : 'Incident Data'}
                  </Button>
                )}
                {!isOnUrlRegistryPage && isOnHotelsPage && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => navigate('/hotels/url-registry')}
                    data-testid="cmd-url-registry"
                    className="hidden sm:flex border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                  >
                    <Link2 className="w-4 h-4 mr-2" />
                    {i18n.language === 'fr' ? 'Registre URL' : 'URL Registry'}
                  </Button>
                )}
                <Button
                  variant={isOnAdminPage ? "default" : "outline"}
                  size="sm"
                  onClick={() => navigate(isOnAdminPage ? '/hotels' : '/admin')}
                  data-testid="cmd-quick-nav"
                  className="hidden sm:flex"
                >
                {isOnAdminPage ? (
                  <>
                    <LayoutDashboard className="w-4 h-4 mr-2" />
                    {i18n.language === 'fr' ? 'Tableau de Bord' : 'Dashboard'}
                  </>
                ) : (
                  <>
                    <Shield className="w-4 h-4 mr-2" />
                    {i18n.language === 'fr' ? 'Contrôle' : 'Control'}
                  </>
                )}
              </Button>
              </>
            )}
            
            {/* Switch Workspace - ONLY for Super Admin */}
            {user?.role === 'super_admin' && hasMultipleWorkspaces && hasMultipleWorkspaces() && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleSwitchWorkspace}
                data-testid="cmd-switch-workspace"
                className="hidden sm:flex text-muted-foreground hover:text-foreground"
              >
                <ArrowLeftRight className="w-4 h-4 mr-2" />
                {i18n.language === 'fr' ? 'Changer' : 'Switch'}
              </Button>
            )}
            
            {/* Language Selector */}
            <LanguageSelector variant="compact" />
            
            {/* Theme Toggle */}
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="cmd-theme-toggle"
              className="text-muted-foreground hover:text-foreground"
            >
              {isDark ? (
                <Sun className="w-4 h-4" />
              ) : (
                <Moon className="w-4 h-4" />
              )}
            </Button>
            
            {/* Account Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="sm"
                  data-testid="cmd-account-menu"
                  className="text-muted-foreground hover:text-foreground"
                >
                  <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center mr-2">
                    <User className="w-4 h-4 text-primary" />
                  </div>
                  <span className="hidden sm:inline text-sm max-w-[120px] truncate">
                    {user?.name || user?.email || 'Account'}
                  </span>
                  <ChevronDown className="w-4 h-4 ml-1" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-56">
                <div className="px-2 py-1.5">
                  <p className="text-sm font-medium text-foreground">
                    {user?.name || 'User'}
                  </p>
                  <p className="text-xs text-muted-foreground truncate">
                    {user?.email}
                  </p>
                  {user?.role === 'super_admin' && (
                    <span className="inline-flex items-center px-1.5 py-0.5 mt-1 rounded text-xs font-medium bg-purple-500/10 text-purple-500">
                      Super Admin
                    </span>
                  )}
                </div>
                <DropdownMenuSeparator />
                {user?.role === 'super_admin' && (
                  <>
                    <DropdownMenuItem
                      onClick={() => navigate('/admin')}
                      data-testid="cmd-admin-dashboard"
                      className="cursor-pointer"
                    >
                      <Settings className="w-4 h-4 mr-2" />
                      {i18n.language === 'fr' ? 'Centre de Contrôle' : 'Control Center'}
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      onClick={() => navigate('/hotels/url-registry')}
                      data-testid="cmd-url-registry-menu"
                      className="cursor-pointer"
                    >
                      <Link2 className="w-4 h-4 mr-2" />
                      {i18n.language === 'fr' ? 'Registre URL' : 'URL Registry'}
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                  </>
                )}
                <DropdownMenuItem
                  onClick={() => navigate('/account')}
                  data-testid="cmd-account-settings"
                  className="cursor-pointer"
                >
                  <Settings className="w-4 h-4 mr-2" />
                  {i18n.language === 'fr' ? 'Paramètres du compte' : 'Account Settings'}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={handleLogout}
                  data-testid="cmd-logout"
                  className="cursor-pointer text-destructive focus:text-destructive"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  {i18n.language === 'fr' ? 'Déconnexion' : 'Sign Out'}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </div>
    </header>
  );
}
